import { useEffect, useState } from "react";
import type { CoveragePartyInput } from "@/lib/coverage/coverageLookup";
import type { CoverageSuggestion } from "@/lib/coverage/coverageNotes";
import type { CoverageQuote } from "@/lib/coverage/coverageSales";
import { saveCoverageQuote } from "@/lib/coverage/coverageClient";

type Props = {
  address: string;
  program: string;
  sizeLabel: string;
  point: { lat: number; lng: number } | null;
  precision: "rooftop" | "city" | null;
  suggestion: CoverageSuggestion | null;
  chosenPartyId: string | null;
  parties: CoveragePartyInput[];
  quotes: CoverageQuote[];
  onSaved: () => void;
};

const field = "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

export function CoverageQuoteCard(props: Props) {
  const [price, setPrice] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = props.parties.find((party) => party.id === props.chosenPartyId);

  useEffect(() => {
    if (props.suggestion?.reason) setReason(props.suggestion.reason);
  }, [props.suggestion?.reason]);

  async function save() {
    if (!props.point || !props.precision || !props.chosenPartyId) return;
    setBusy(true);
    setError(null);
    const res = await saveCoverageQuote({
      address_text: props.address,
      lat: props.point.lat,
      lng: props.point.lng,
      precision: props.precision,
      program: props.program,
      size_label: props.sizeLabel,
      suggested_party_id: props.suggestion?.partyId ?? "",
      chosen_party_id: props.chosenPartyId,
      price_usd: price,
      lead_time: leadTime,
      reason,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setPrice("");
    setLeadTime("");
    props.onSaved();
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Quote on this pin</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
        {chosen ? `Chosen provider: ${chosen.name}` : "Choose a provider above, then record the price."}
      </p>
      <div className="mt-2 space-y-2">
        <label className="block text-xs text-gray-500">
          Price dollars
          <input className={field} value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
        <label className="block text-xs text-gray-500">
          Lead time
          <input className={field} value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="Two weeks" />
        </label>
        <label className="block text-xs text-gray-500">
          Why this provider
          <textarea className={field} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <button
          type="button"
          disabled={busy || !props.point || !props.chosenPartyId || !price.trim()}
          className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save quote"}
        </button>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
      <ul className="mt-3 max-h-28 space-y-1 overflow-auto text-sm text-gray-800 dark:text-zinc-100">
        {props.quotes.map((quote) => {
          const name = props.parties.find((party) => party.id === quote.chosenPartyId)?.name ?? "Provider";
          return (
            <li key={quote.id}>
              {name} · ${quote.priceUsd ?? "—"} · {quote.leadTime || "lead time not set"}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
